import { useSyncExternalStore } from "react";
import { Role } from "../gen/gd_platform_pb";
import { useViewer } from "../shell-data";

/** An account someone works as: their own (personal) or an organisation they belong to. */
export type Owner = { login: string; name: string; personal: boolean; role: Role; repositories: number; agents: number };

/** The viewer's own account first, then their organisations. */
export function useOwners(): Owner[] {
  const v = useViewer().data;
  if (!v?.account) return [];
  const me: Owner = { login: v.account.login, name: v.account.displayName || v.account.login, personal: true, role: Role.ADMIN, repositories: 0, agents: 0 };
  return [
    me,
    ...v.organizations.map((o) => ({ login: o.login, name: o.displayName || o.login, personal: false, role: o.viewerRole, repositories: o.repositories, agents: o.agents })),
  ];
}

// The account the sidebar shows, kept per browser; pages that belong to one of the viewer's
// accounts switch it as they open (see AppShell).
const KEY = "gd.context";
const listeners = new Set<() => void>();
let current: string | null = null;
try {
  current = localStorage.getItem(KEY);
} catch {
  // no storage: the default
}

export function setAccountContext(login: string) {
  if (login === current) return;
  current = login;
  try {
    localStorage.setItem(KEY, login);
  } catch {
    // kept for this page only
  }
  listeners.forEach((l) => l());
}

/**
 * The current account among `owners`: the one chosen, else the first that has repositories
 * (`owning` are the owners of the repositories the viewer sees), else the viewer's own.
 */
export function useAccountContext(owners: Owner[], owning?: string[]): Owner | undefined {
  const chosen = useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => current,
  );
  return owners.find((o) => o.login === chosen) ?? owners.find((o) => owning?.includes(o.login)) ?? owners[0];
}
